import React from "react";
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Alert,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { Footer, Header, CustomSnackbar } from "../../shared"; // Adjust path
import { rolesAdmin, useSecurityCheck } from "../../../utils"; // Adjust path
import { motion } from "framer-motion";

import { useUtility } from "utils/context/UtilityContext"; // Adjust path
import { useMottoManager } from "utils/hooks/useMottoManager";
import { MottoDialog } from "features/info/MottoDialog";

export const MottoManager = () => {
  const securityCheck = useSecurityCheck(rolesAdmin);
  const {
    mottos,
    loading,
    isSaving,
    openDialog,
    currentMotto,
    snackbar,
    handleSaveMotto,
    handleDeleteMotto,
    handleSnackbarClose,
    handleOpenAddDialog,
    handleOpenEditDialog,
    handleCloseDialog,
  } = useMottoManager();

  const { marginBottomHeader } = useUtility();

  if (securityCheck) {
    return securityCheck;
  }

  const renderContent = () => {
    if (loading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="50vh"
        >
          <CircularProgress />
        </Box>
      );
    }

    if (mottos.length === 0) {
      return (
        <Alert severity="info">
          Aucun motto trouvé. Commencez par en ajouter un.
        </Alert>
      );
    }

    return (
      <Paper elevation={1}>
        <List>
          {mottos.map((motto) => (
            <ListItem
              key={motto.id}
              divider
              sx={{
                flexWrap: "wrap",
                "& .MuiListItemSecondaryAction-root": {
                  position: { xs: "relative", sm: "absolute" },
                  transform: { xs: "translateY(0)", sm: "translateY(-50%)" },
                  top: { sm: "50%" },
                  right: { sm: 16 },
                  width: { xs: "100%", sm: "auto" },
                  textAlign: { xs: "right", sm: "unset" },
                  mt: { xs: 1, sm: 0 },
                },
              }}
            >
              <ListItemText
                primary={
                  <div dangerouslySetInnerHTML={{ __html: motto.motto }} />
                }
                sx={{ overflow: "hidden", pr: { sm: "100px" } }}
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  aria-label="edit"
                  onClick={() => handleOpenEditDialog(motto)}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  edge="end"
                  aria-label="delete"
                  onClick={() => handleDeleteMotto(motto.id!)}
                >
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </Paper>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={`flex flex-col min-h-screen ${marginBottomHeader}`}
    >
      <Header />

      <Container maxWidth="lg" sx={{ flexGrow: 1, py: { xs: 3, md: 5 } }}>
        <Stack spacing={3}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
          >
            <Typography variant="h4" component="h1">
              Gestion des Mottos
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenAddDialog}
            >
              Ajouter un Motto
            </Button>
          </Box>

          {renderContent()}
        </Stack>
      </Container>

      <Footer />

      <MottoDialog
        open={openDialog}
        isSaving={isSaving}
        onClose={handleCloseDialog}
        onSave={handleSaveMotto}
        initialData={currentMotto}
      />
      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        isSuccess={snackbar.severity === "success"}
        handleClose={handleSnackbarClose}
      />
    </motion.div>
  );
};
